import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowRight, Sparkles, Instagram, Music, Youtube,
  ShieldCheck, BarChart3, Building2, ShoppingBag, CheckCircle2,
  Lock, ChevronRight, Zap, TrendingUp,
} from 'lucide-react';

import { Donut, HorizontalBar } from '../components/charts/ReportChart';
import { Button, Badge } from '../components/ui/light';

// ──────────────────────────────────────────────────────────────────────
// Demo content used in the hero preview
// ──────────────────────────────────────────────────────────────────────

const HERO_DEMO_INTERESTS = [
  { name: 'Beauty & Cosmetics', value: 42 },
  { name: 'Fashion',            value: 31 },
  { name: 'Travel',             value: 22 },
  { name: 'Wellness',           value: 17 },
];

const HERO_DEMO_REVENUE = [
  { name: 'Brand deals',      value: 38 },
  { name: 'Affiliate',        value: 18 },
  { name: 'Product sales',    value: 26 },
  { name: 'Digital products', value: 12 },
  { name: 'Memberships',      value: 6 },
];

const HOW_IT_WORKS = [
  { step: '1', title: 'Paste a link',   body: 'Any Instagram, TikTok, or YouTube profile.' },
  { step: '2', title: 'We analyze',     body: 'Audience, content, and monetization in 30 seconds.' },
  { step: '3', title: 'Unlock more',    body: 'Upgrade for product, vendor, and brand-deal matches.' },
];

const CAPABILITIES = [
  { icon: BarChart3,   title: 'Audience intelligence',   body: 'Gender, age, geography, interests, brand affinity, and authenticity score — for any social profile.' },
  { icon: ShoppingBag, title: 'Product & vendor match',  body: 'Top products and pre-vetted vendors ranked to your audience, with score breakdowns and GMV bands.' },
  { icon: TrendingUp,  title: 'Monetization roadmap',    body: 'A prioritized playbook across brand deals, affiliate, products, digital, and memberships.' },
  { icon: Building2,   title: 'Cohort benchmarks',       body: 'See exactly where you sit against peers in your niche, follower band, and platform.' },
];

const PRICING = [
  {
    id: 'free' as const,
    name: 'Free',
    price: 0,
    tagline: 'Try the report experience',
    features: [
      '5 free reports lifetime',
      'Audience intelligence',
      'Cohort income bands',
      'Basic monetization overview',
    ],
    cta: 'Generate free report',
  },
  {
    id: 'pro' as const,
    name: 'Pro',
    price: 20,
    tagline: 'Unlock the full playbook',
    badge: 'Most popular',
    features: [
      'Unlimited premium reports',
      'Product matches with score breakdown',
      'Vendor matches with commission ranges',
      'Posting timing & content intelligence',
      'Full monetization breakdown',
      '6+ priority actions per report',
    ],
    cta: 'Start with Pro',
  },
  {
    id: 'scale' as const,
    name: 'Scale',
    price: 40,
    tagline: 'For full-time creators',
    features: [
      'Everything in Pro',
      'Brand-deal pipeline',
      'Estimated value bands per brand',
      'Priority opportunities',
      'Quarterly performance review',
    ],
    cta: 'Start with Scale',
  },
];

// ──────────────────────────────────────────────────────────────────────

export default function Landing() {
  const navigate = useNavigate();
  const [profileUrl, setProfileUrl] = useState('');
  const [email, setEmail] = useState('');

  const startFlow = () => {
    const params = new URLSearchParams();
    if (profileUrl.trim()) params.set('profile_url', profileUrl.trim());
    if (email.trim())      params.set('email',       email.trim());
    navigate(`/signup?${params.toString()}`);
  };

  return (
    <div className="theme-light min-h-screen" style={{ background: 'var(--ch2-bg)' }}>
      {/* ── Top nav ─────────────────────────────────────────────── */}
      <header
        className="sticky top-0 z-30 backdrop-blur-md"
        style={{
          background: 'rgba(250,250,247,0.85)',
          borderBottom: '1px solid var(--ch2-border)',
        }}
      >
        <div className="l-shell flex items-center justify-between py-4">
          <Wordmark />
          <nav className="hidden md:flex items-center gap-8 text-sm" style={{ color: 'var(--ch2-ink-2)' }}>
            <a href="#how"      className="hover:opacity-70 transition-opacity">How it works</a>
            <a href="#preview"  className="hover:opacity-70 transition-opacity">Inside the report</a>
            <a href="#features" className="hover:opacity-70 transition-opacity">Features</a>
            <a href="#pricing"  className="hover:opacity-70 transition-opacity">Pricing</a>
            <Link to="/demo-report" className="hover:opacity-70 transition-opacity">View sample</Link>
          </nav>
          <div className="flex items-center gap-2">
            <Link to="/login"><Button size="sm" variant="ghost">Sign in</Button></Link>
            <Button size="sm" variant="primary" onClick={startFlow}>Get free report</Button>
          </div>
        </div>
      </header>

      {/* ── Hero ─────────────────────────────────────────────────
           NOTE: section is NOT overflow-hidden — the floating
           visual extends outside the column on wide screens. */}
      <section className="relative">
        {/* Soft brand-tinted backdrop behind the hero */}
        <div
          aria-hidden
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              'radial-gradient(900px 480px at 80% -10%, rgba(91,91,214,0.12), transparent 60%),' +
              'radial-gradient(700px 420px at 0% 110%, rgba(255,212,0,0.10), transparent 55%)',
          }}
        />

        <div className="l-shell relative pt-16 lg:pt-24 pb-20 lg:pb-28 grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          {/* LEFT — copy + form (flipped order: copy first, more direct) */}
          <div className="lg:col-span-6 relative">
            <Badge variant="accent" className="mb-6">
              <Sparkles className="w-3 h-3 mr-1 inline" />
              Now in beta · 30-second free reports
            </Badge>

            <h1
              className="font-display font-bold tracking-tight mb-6"
              style={{
                fontSize: 'clamp(40px, 5.6vw, 68px)',
                lineHeight: 1.02,
                letterSpacing: '-0.025em',
                color: 'var(--ch2-ink)',
              }}
            >
              See exactly how much<br />
              your audience<br />
              is&nbsp;<span style={{ color: 'var(--ch2-accent)' }}>worth.</span>
            </h1>

            <p className="text-lg max-w-xl leading-relaxed mb-8" style={{ color: 'var(--ch2-ink-2)' }}>
              Paste any social profile. Get a polished monetization intelligence report with audience deep‑dive,
              cohort income bands, and the exact opportunities you can unlock.
            </p>

            <form
              className="space-y-3 max-w-md"
              onSubmit={(e) => { e.preventDefault(); startFlow(); }}
            >
              <input
                type="url"
                className="l-input"
                placeholder="https://instagram.com/your-handle"
                value={profileUrl}
                onChange={(e) => setProfileUrl(e.target.value)}
                autoComplete="off"
              />
              <input
                type="email"
                className="l-input"
                placeholder="you@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
              />
              <Button
                type="submit"
                variant="accent"
                size="lg"
                className="w-full"
                iconRight={<ArrowRight className="w-4 h-4" />}
              >
                Generate my free report
              </Button>
            </form>

            <div className="flex items-center gap-5 text-xs flex-wrap mt-5" style={{ color: 'var(--ch2-ink-3)' }}>
              <span className="inline-flex items-center gap-1.5"><Zap        className="w-3.5 h-3.5" /> 30 seconds</span>
              <span className="inline-flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5" /> No spam</span>
              <span className="inline-flex items-center gap-1.5"><Lock        className="w-3.5 h-3.5" /> No credit card</span>
            </div>

            <div className="mt-5">
              <Link
                to="/demo-report"
                className="inline-flex items-center gap-1.5 text-sm font-medium"
                style={{ color: 'var(--ch2-accent-2)' }}
              >
                See a sample report <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>

            <div className="flex items-center gap-2 mt-6">
              <span className="text-xs uppercase tracking-wider mr-2" style={{ color: 'var(--ch2-ink-3)' }}>
                Works with
              </span>
              <Badge variant="neutral"><Instagram className="w-3 h-3 mr-1 inline" />Instagram</Badge>
              <Badge variant="neutral"><Music      className="w-3 h-3 mr-1 inline" />TikTok</Badge>
              <Badge variant="neutral"><Youtube    className="w-3 h-3 mr-1 inline" />YouTube</Badge>
            </div>
          </div>

          {/* RIGHT — visual report mock. Wrapped in extra padding so any
               floating chips never get clipped by the column. */}
          <div className="lg:col-span-6 relative" style={{ paddingTop: 14, paddingBottom: 14 }}>
            <HeroReportPreview />
          </div>
        </div>
      </section>

      {/* ── Logo / trust strip ─────────────────────────────────── */}
      <section className="py-10" style={{ background: 'var(--ch2-surface)', borderTop: '1px solid var(--ch2-border)', borderBottom: '1px solid var(--ch2-border)' }}>
        <div className="l-shell flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="text-xs uppercase tracking-widest" style={{ color: 'var(--ch2-ink-3)' }}>
            Powering creator monetization
          </div>
          <div className="flex flex-wrap items-center gap-x-10 gap-y-3" style={{ color: 'var(--ch2-ink-2)' }}>
            <BrandLine icon={<Instagram className="w-4 h-4" />} label="Instagram" />
            <BrandLine icon={<Music     className="w-4 h-4" />} label="TikTok" />
            <BrandLine icon={<Youtube   className="w-4 h-4" />} label="YouTube" />
            <BrandLine icon={<ShoppingBag className="w-4 h-4" />} label="Shopify" />
            <BrandLine icon={<Sparkles  className="w-4 h-4" />} label="Modash" />
          </div>
        </div>
      </section>

      {/* ── How it works (3 steps, inline) ───────────────────── */}
      <section id="how" className="py-20 lg:py-24">
        <div className="l-shell">
          <div className="max-w-2xl mb-12">
            <div className="l-section-eyebrow mb-3">How it works</div>
            <h2 className="font-display font-bold tracking-tight" style={{ fontSize: 'clamp(28px, 3.8vw, 44px)', lineHeight: 1.1, color: 'var(--ch2-ink)', letterSpacing: '-0.02em' }}>
              From a single link to a complete monetization roadmap.
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            {HOW_IT_WORKS.map((s, i) => (
              <motion.div
                key={s.step}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05 }}
                className="l-card p-7"
              >
                <div
                  className="w-9 h-9 flex items-center justify-center mb-5 font-display font-bold"
                  style={{
                    background: 'var(--ch2-ink)',
                    color: '#FFD400',
                    borderRadius: 999,
                    fontSize: 14,
                  }}
                >
                  {s.step}
                </div>
                <h3 className="font-semibold mb-1.5" style={{ color: 'var(--ch2-ink)', fontSize: 17 }}>{s.title}</h3>
                <p className="text-sm" style={{ color: 'var(--ch2-ink-2)' }}>{s.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Inside the report (one polished mock + 4 anchors) ── */}
      <section id="preview" className="py-20 lg:py-24" style={{ background: 'var(--ch2-surface)' }}>
        <div className="l-shell">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-start">
            <div className="lg:col-span-5">
              <div className="l-section-eyebrow mb-3">Inside the report</div>
              <h2 className="font-display font-bold tracking-tight mb-4" style={{ fontSize: 'clamp(28px, 3.8vw, 44px)', lineHeight: 1.1, color: 'var(--ch2-ink)', letterSpacing: '-0.02em' }}>
                Eight sections.<br />One income answer.
              </h2>
              <p className="mb-6 text-base" style={{ color: 'var(--ch2-ink-2)' }}>
                Every report covers audience intelligence, cohort benchmarks, monetization estimates, and a prioritized
                action plan. Pro and Scale unlock product matches, vendor matches, and the brand-deal pipeline.
              </p>
              <ul className="space-y-2.5">
                {[
                  'Creator Snapshot · Audience Intelligence',
                  'Cohort Benchmark · Monetization',
                  'Content & Performance signals',
                  'Product / Vendor / Brand matches (Pro)',
                ].map((line) => (
                  <li key={line} className="flex items-start gap-2.5 text-sm" style={{ color: 'var(--ch2-ink)' }}>
                    <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" style={{ color: 'var(--ch2-accent)' }} />
                    {line}
                  </li>
                ))}
              </ul>
            </div>

            <div className="lg:col-span-7">
              <ReportPreviewBig />
            </div>
          </div>
        </div>
      </section>

      {/* ── Capabilities grid (4) ───────────────────────────── */}
      <section id="features" className="py-20 lg:py-24">
        <div className="l-shell">
          <div className="max-w-2xl mb-12">
            <div className="l-section-eyebrow mb-3">What CreatorHub does</div>
            <h2 className="font-display font-bold tracking-tight" style={{ fontSize: 'clamp(28px, 3.8vw, 44px)', lineHeight: 1.1, color: 'var(--ch2-ink)', letterSpacing: '-0.02em' }}>
              The full creator monetization stack — in one place.
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-5">
            {CAPABILITIES.map((c) => (
              <div key={c.title} className="l-card p-7 flex gap-4 items-start">
                <div
                  className="w-10 h-10 flex items-center justify-center shrink-0"
                  style={{
                    background: 'var(--ch2-accent-tint)',
                    color: 'var(--ch2-accent-2)',
                    borderRadius: 'var(--ch2-radius-sm)',
                  }}
                >
                  <c.icon className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="font-semibold mb-1" style={{ color: 'var(--ch2-ink)', fontSize: 17 }}>{c.title}</h3>
                  <p className="text-sm" style={{ color: 'var(--ch2-ink-2)' }}>{c.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Pricing ─────────────────────────────────────────────
           NOTE: padding-top is generous so the "Most popular"
           ribbon on the Pro card never gets clipped. The Pro card
           uses an internal ribbon (not absolute -top-3) so it
           always lays out cleanly inside the card frame. */}
      <section id="pricing" className="py-20 lg:py-28" style={{ background: 'var(--ch2-surface)' }}>
        <div className="l-shell">
          <div className="text-center mb-14 max-w-2xl mx-auto">
            <div className="l-section-eyebrow mb-3">Pricing</div>
            <h2 className="font-display font-bold tracking-tight mb-4" style={{ fontSize: 'clamp(28px, 3.8vw, 44px)', lineHeight: 1.1, color: 'var(--ch2-ink)', letterSpacing: '-0.02em' }}>
              Simple, transparent, creator-first.
            </h2>
            <p className="text-base" style={{ color: 'var(--ch2-ink-2)' }}>
              Start free. Upgrade when you want the full playbook.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {PRICING.map((p) => (
              <PricingCard key={p.id} plan={p} onCta={startFlow} />
            ))}
          </div>
        </div>
      </section>

      {/* ── Final CTA ───────────────────────────────────────── */}
      <section className="py-24 lg:py-32" style={{ background: 'var(--ch2-bg)' }}>
        <div className="l-shell">
          <div
            className="px-8 py-14 lg:px-16 lg:py-20 text-center relative overflow-hidden"
            style={{
              background: 'var(--ch2-ink)',
              color: '#ffffff',
              borderRadius: 'var(--ch2-radius-lg)',
              boxShadow: 'var(--ch2-shadow-lg)',
            }}
          >
            {/* Subtle gold halo */}
            <div
              aria-hidden
              className="absolute inset-0 pointer-events-none"
              style={{
                background:
                  'radial-gradient(600px 300px at 50% -20%, rgba(255,212,0,0.18), transparent 60%)',
              }}
            />

            <div className="relative">
              <div className="inline-flex items-center gap-2 mb-5 px-3 py-1" style={{ background: 'rgba(255,212,0,0.10)', borderRadius: 999, border: '1px solid rgba(255,212,0,0.24)' }}>
                <Sparkles className="w-3.5 h-3.5" style={{ color: '#FFD400' }} />
                <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: '#FFD400' }}>
                  Free · 30 seconds · No credit card
                </span>
              </div>

              <h2
                className="font-display font-bold mb-5"
                style={{
                  fontSize: 'clamp(32px, 5vw, 56px)',
                  letterSpacing: '-0.025em',
                  lineHeight: 1.05,
                }}
              >
                Your monetization report<br /> is one paste away.
              </h2>

              <form
                className="flex flex-col sm:flex-row gap-3 max-w-xl mx-auto"
                onSubmit={(e) => { e.preventDefault(); startFlow(); }}
              >
                <input
                  type="url"
                  placeholder="Paste your profile link"
                  className="flex-1 px-4 py-3.5 text-sm"
                  style={{
                    background: 'rgba(255,255,255,0.06)',
                    border: '1px solid rgba(255,255,255,0.18)',
                    borderRadius: 'var(--ch2-radius-sm)',
                    color: '#fff',
                  }}
                  value={profileUrl}
                  onChange={(e) => setProfileUrl(e.target.value)}
                />
                <button
                  type="submit"
                  className="px-7 py-3.5 font-semibold text-sm inline-flex items-center justify-center gap-2"
                  style={{ background: '#FFD400', color: 'var(--ch2-ink)', borderRadius: 'var(--ch2-radius-sm)' }}
                >
                  Generate free report <ArrowRight className="w-4 h-4" />
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* ── Footer ─────────────────────────────────────────── */}
      <footer className="py-10" style={{ background: 'var(--ch2-bg)', borderTop: '1px solid var(--ch2-border)' }}>
        <div className="l-shell flex flex-col md:flex-row items-center justify-between gap-4 text-sm" style={{ color: 'var(--ch2-ink-3)' }}>
          <div className="flex items-center gap-2">
            <Wordmark size="sm" />
            <span className="hidden md:inline opacity-60">© {new Date().getFullYear()}</span>
          </div>
          <div className="flex items-center gap-6">
            <Link to="/legal/terms"   className="hover:underline">Terms</Link>
            <Link to="/legal/privacy" className="hover:underline">Privacy</Link>
            <Link to="/login"         className="hover:underline">Sign in</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Wordmark — distinct brand mark (square + diagonal "/" feels less generic)
// ──────────────────────────────────────────────────────────────────────

function Wordmark({ size = 'md' }: { size?: 'sm' | 'md' }) {
  const dim   = size === 'sm' ? 22 : 28;
  const text  = size === 'sm' ? 14 : 17;
  const inner = size === 'sm' ? 10 : 12;
  return (
    <div className="flex items-center gap-2.5">
      <div
        className="flex items-center justify-center font-display font-bold relative overflow-hidden"
        style={{
          width: dim,
          height: dim,
          background: 'var(--ch2-ink)',
          color: '#FFD400',
          borderRadius: 6,
          fontSize: inner,
          letterSpacing: '-0.04em',
        }}
      >
        <span style={{ position: 'relative', zIndex: 2 }}>C</span>
        {/* diagonal slash accent */}
        <span
          aria-hidden
          style={{
            position: 'absolute',
            right: -2,
            bottom: -2,
            width: dim * 0.6,
            height: 2,
            background: '#FFD400',
            transform: 'rotate(-45deg)',
            transformOrigin: 'bottom right',
          }}
        />
      </div>
      <span
        className="font-display font-semibold tracking-tight"
        style={{ fontSize: text, color: 'var(--ch2-ink)', letterSpacing: '-0.015em' }}
      >
        CreatorHub<span style={{ color: 'var(--ch2-accent)' }}>360</span>
      </span>
    </div>
  );
}

function BrandLine({ icon, label }: { icon: React.ReactNode; label: string }) {
  return (
    <span className="inline-flex items-center gap-2 text-sm font-medium opacity-80">
      {icon}{label}
    </span>
  );
}

// ──────────────────────────────────────────────────────────────────────
// HeroReportPreview — compact, branded report card
// ──────────────────────────────────────────────────────────────────────

function HeroReportPreview() {
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.55 }}
      className="relative"
    >
      {/* Decorative offset card behind the main one — subtle depth, not gimmicky */}
      <div
        aria-hidden
        className="absolute inset-0 hidden lg:block"
        style={{
          transform: 'translate(14px, 16px) rotate(1.5deg)',
          background: 'var(--ch2-surface-2)',
          border: '1px solid var(--ch2-border)',
          borderRadius: 'var(--ch2-radius-lg)',
        }}
      />

      <div
        className="relative p-5 lg:p-6"
        style={{
          background: 'var(--ch2-surface)',
          border: '1px solid var(--ch2-border)',
          borderRadius: 'var(--ch2-radius-lg)',
          boxShadow: 'var(--ch2-shadow-lg)',
        }}
      >
        {/* Header row */}
        <div className="flex items-center gap-3 mb-5 pb-4" style={{ borderBottom: '1px solid var(--ch2-border)' }}>
          <div
            className="w-11 h-11 flex items-center justify-center font-display font-bold"
            style={{ background: 'var(--ch2-accent-tint)', color: 'var(--ch2-accent-2)', borderRadius: 999 }}
          >
            EM
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-display font-semibold truncate" style={{ color: 'var(--ch2-ink)', fontSize: 16 }}>
              @ember_kalani
            </div>
            <div className="text-xs" style={{ color: 'var(--ch2-ink-3)' }}>
              Instagram · Beauty creator · 412K followers
            </div>
          </div>
          <Badge variant="success">Tier 2</Badge>
        </div>

        {/* KPI strip */}
        <div className="grid grid-cols-4 gap-2.5 mb-4">
          {[
            { label: 'Followers', value: '412K' },
            { label: 'ER',        value: '4.8%' },
            { label: 'Cohort',    value: 'P83' },
            { label: 'Yr GMV',    value: '$148K' },
          ].map((k) => (
            <div
              key={k.label}
              className="px-2.5 py-2"
              style={{ background: 'var(--ch2-surface-2)', borderRadius: 'var(--ch2-radius-sm)' }}
            >
              <div className="l-section-eyebrow" style={{ fontSize: 10 }}>{k.label}</div>
              <div className="font-display font-bold mt-1" style={{ color: 'var(--ch2-ink)', fontSize: 15 }}>
                {k.value}
              </div>
            </div>
          ))}
        </div>

        {/* Charts row */}
        <div className="grid grid-cols-2 gap-2.5 mb-4">
          <div className="p-2.5" style={{ background: 'var(--ch2-surface-2)', borderRadius: 'var(--ch2-radius-sm)' }}>
            <div className="l-section-eyebrow mb-1.5" style={{ fontSize: 10 }}>Revenue mix</div>
            <Donut data={HERO_DEMO_REVENUE} height={130} />
          </div>
          <div className="p-2.5" style={{ background: 'var(--ch2-surface-2)', borderRadius: 'var(--ch2-radius-sm)' }}>
            <div className="l-section-eyebrow mb-1.5" style={{ fontSize: 10 }}>Audience interests</div>
            <HorizontalBar data={HERO_DEMO_INTERESTS} valueFormatter={(v) => `${v}%`} height={130} />
          </div>
        </div>

        {/* Action item */}
        <div
          className="p-3 flex items-start gap-3"
          style={{
            background: 'var(--ch2-accent-tint)',
            border: '1px solid rgba(91,91,214,0.18)',
            borderRadius: 'var(--ch2-radius-sm)',
          }}
        >
          <div
            className="w-7 h-7 flex items-center justify-center font-bold shrink-0"
            style={{ background: 'var(--ch2-accent)', color: '#fff', borderRadius: 999, fontSize: 12 }}
          >
            1
          </div>
          <div>
            <div className="font-semibold text-sm" style={{ color: 'var(--ch2-ink)' }}>
              Pilot a private-label SKU in beauty
            </div>
            <div className="text-xs mt-0.5" style={{ color: 'var(--ch2-ink-3)' }}>
              Effort: medium · Impact: high · Yr-1 GMV: $45K
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Larger preview block used in the "Inside the report" section
// ──────────────────────────────────────────────────────────────────────

function ReportPreviewBig() {
  const sections = [
    { id: '01', title: 'Creator Snapshot',    locked: false },
    { id: '02', title: 'Audience',            locked: false },
    { id: '03', title: 'Cohort Benchmark',    locked: false },
    { id: '04', title: 'Monetization',        locked: false },
    { id: '05', title: 'Content Signals',     locked: false },
    { id: '06', title: 'Product Matches',     locked: true  },
    { id: '07', title: 'Vendor Matches',      locked: true  },
    { id: '08', title: 'Recommended Actions', locked: false },
  ];
  return (
    <div
      className="p-5 lg:p-6"
      style={{
        background: 'var(--ch2-bg)',
        border: '1px solid var(--ch2-border)',
        borderRadius: 'var(--ch2-radius-lg)',
        boxShadow: 'var(--ch2-shadow-md)',
      }}
    >
      <div className="grid sm:grid-cols-2 gap-3 mb-5">
        <div className="p-4" style={{ background: 'var(--ch2-surface)', border: '1px solid var(--ch2-border)', borderRadius: 'var(--ch2-radius-md)' }}>
          <div className="l-section-eyebrow mb-2">Revenue mix</div>
          <Donut data={HERO_DEMO_REVENUE} height={170} />
        </div>
        <div className="p-4" style={{ background: 'var(--ch2-surface)', border: '1px solid var(--ch2-border)', borderRadius: 'var(--ch2-radius-md)' }}>
          <div className="l-section-eyebrow mb-2">Audience interests</div>
          <HorizontalBar data={HERO_DEMO_INTERESTS} valueFormatter={(v) => `${v}%`} height={170} />
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {sections.map((s) => (
          <div
            key={s.id}
            className="px-3 py-2.5 flex items-center gap-2"
            style={{
              background: s.locked ? 'var(--ch2-surface-2)' : 'var(--ch2-surface)',
              border: '1px solid var(--ch2-border)',
              borderRadius: 'var(--ch2-radius-sm)',
            }}
          >
            <span className="l-section-eyebrow" style={{ fontSize: 10 }}>{s.id}</span>
            <span className="font-medium text-xs" style={{ color: 'var(--ch2-ink)' }}>{s.title}</span>
            {s.locked && <Lock className="w-3 h-3 ml-auto" style={{ color: 'var(--ch2-accent-2)' }} />}
          </div>
        ))}
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────
// PricingCard — internal ribbon (not absolute) so layout stays clean
// ──────────────────────────────────────────────────────────────────────

interface Plan {
  id: 'free' | 'pro' | 'scale';
  name: string;
  price: number;
  tagline: string;
  badge?: string;
  features: string[];
  cta: string;
}

function PricingCard({ plan, onCta }: { plan: Plan; onCta: () => void }) {
  const featured = plan.id === 'pro';
  return (
    <div
      className="flex flex-col h-full"
      style={{
        background: featured ? 'var(--ch2-ink)' : 'var(--ch2-surface)',
        color:      featured ? '#ffffff'        : 'var(--ch2-ink)',
        border:     featured ? '1px solid rgba(255,212,0,0.18)' : '1px solid var(--ch2-border)',
        borderRadius: 'var(--ch2-radius-lg)',
        boxShadow: featured ? '0 24px 56px rgba(11,11,11,0.18)' : 'var(--ch2-shadow-sm)',
        overflow: 'hidden',
      }}
    >
      {plan.badge ? (
        <div
          className="px-7 py-2.5 flex items-center justify-between text-xs font-semibold uppercase tracking-wider"
          style={{
            background: 'rgba(255,212,0,0.12)',
            color: '#FFD400',
            borderBottom: '1px solid rgba(255,212,0,0.20)',
            letterSpacing: '0.10em',
          }}
        >
          <span className="inline-flex items-center gap-1.5"><Sparkles className="w-3 h-3" /> {plan.badge}</span>
          <span style={{ opacity: 0.55 }}>Save 15% yearly</span>
        </div>
      ) : null}

      <div className="px-7 pt-7 pb-6 flex flex-col flex-1">
        <div className="text-xs uppercase tracking-wider mb-2" style={{ opacity: 0.55 }}>{plan.name}</div>
        <div className="font-display font-bold mb-1 flex items-baseline gap-1.5" style={{ fontSize: 44, lineHeight: 1, letterSpacing: '-0.02em' }}>
          ${plan.price}
          <span className="text-sm font-medium" style={{ opacity: 0.55 }}>/mo</span>
        </div>
        <div className="text-sm mb-6" style={{ opacity: 0.7 }}>{plan.tagline}</div>

        <ul className="space-y-2.5 mb-7 flex-1">
          {plan.features.map((f) => (
            <li key={f} className="flex items-start gap-2.5 text-sm">
              <CheckCircle2
                className="w-4 h-4 mt-0.5 shrink-0"
                style={{ color: featured ? '#FFD400' : 'var(--ch2-accent)' }}
              />
              <span style={{ opacity: featured ? 0.95 : 1 }}>{f}</span>
            </li>
          ))}
        </ul>

        <button
          onClick={onCta}
          className="w-full inline-flex items-center justify-center gap-1.5 font-semibold text-sm py-3"
          style={{
            background: featured ? '#FFD400' : 'var(--ch2-ink)',
            color:      featured ? 'var(--ch2-ink)' : '#ffffff',
            borderRadius: 'var(--ch2-radius-sm)',
          }}
        >
          {plan.cta} <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
